import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { FactionMapScene } from '../game/scenes/FactionMapScene';
import './FactionWarfareMap.css';

interface FactionWarfareMapWrapperProps {
  onClose: () => void;
}

interface IslandData {
  id: string;
  x: number;
  y: number;
  radius: number;
  type: string;
  biome: string;
  ownerId: string | null;
  ownerType: string | null;
  ownerName: string | null;
  ownerShipColor: string | null;
  factionId: string | null;
  permanentName: string | null;
  customName: string | null;
  claimRadius: number | null;
  isPermanent: boolean;
  assignedFaction?: string | null;
}

interface FactionWarfareMapWrapperProps {
  onClose: () => void;
}

export const FactionWarfareMapWrapper: React.FC<FactionWarfareMapWrapperProps> = ({ onClose }) => {
  const gameRef = useRef<Phaser.Game | null>(null);
  const sceneRef = useRef<FactionMapScene | null>(null);
  const didInitSceneRef = useRef<boolean>(false);
  const [territoryCount, setTerritoryCount] = useState<number>(0);
  const [selectedIsland, setSelectedIsland] = useState<IslandData | null>(null);
  const [factionStats, setFactionStats] = useState<Record<string, number>>({});
  const islandsDataRef = useRef<IslandData[]>([]);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const handleForceRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      console.log('[FACTION MAP] Force refresh initiated');
      
      // Destroy and recreate the scene to force a full reload
      if (gameRef.current && sceneRef.current) {
        console.log('[FACTION MAP] Destroying current scene');
        gameRef.current.scene.remove('FactionMapScene');
        gameRef.current.scene.add('FactionMapScene', FactionMapScene, true);
        
        // Wait for scene to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Re-attach event listeners
        const newScene = gameRef.current.scene.getScene('FactionMapScene') as FactionMapScene;
        if (newScene) {
          sceneRef.current = newScene;
          
          newScene.events.on('mapLoaded', (count: number) => {
            setTerritoryCount(count);
            const stats: Record<string, number> = {};
            const islands = newScene.getIslands();
            islandsDataRef.current = islands;
            
            islands.forEach((island: IslandData) => {
              let faction = 'unclaimed';
              if ((island as any).assignedFaction) {
                faction = (island as any).assignedFaction;
              } else if (island.ownerType === 'faction_regime') {
                faction = 'regime';
              } else if (island.factionId) {
                faction = island.factionId;
              } else if (island.ownerId?.startsWith('faction_')) {
                faction = island.ownerId.replace('faction_', '');
              } else if (island.ownerId) {
                faction = 'player';
              }
              stats[faction] = (stats[faction] || 0) + 1;
            });
            setFactionStats(stats);
            console.log('[FACTION MAP] Force refresh completed, stats:', stats);
          });
          
          newScene.events.on('islandSelected', (island: IslandData) => {
            console.log('[FACTION MAP] Island selected:', island.id);
            setSelectedIsland(island);
          });
          
          newScene.events.on('islandDeselected', () => {
            setSelectedIsland(null);
          });
        }
      }
    } catch (error) {
      console.error('[FACTION MAP] Force refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: window.innerWidth,
      height: window.innerHeight - 100,
      parent: 'faction-map-canvas',
      backgroundColor: '#0a0e1a',
      scene: [FactionMapScene],
      render: { antialias: false, pixelArt: true, roundPixels: true },
    };

    gameRef.current = new Phaser.Game(config);

    const initScene = (): void => {
      if (didInitSceneRef.current) return;
      const scene = gameRef.current?.scene.getScene('FactionMapScene') as FactionMapScene | undefined;
      if (!scene) return;
      
      sceneRef.current = scene;
      didInitSceneRef.current = true;
      
      // Attach event listeners
      scene.events.on('mapLoaded', (count: number) => {
        setTerritoryCount(count);
        // Calculate faction stats from islands
        const stats: Record<string, number> = {};
        const islands = scene.getIslands();
        
        // Cache islands data for consistency
        islandsDataRef.current = islands;
        
        islands.forEach((island: IslandData) => {
          let faction = 'unclaimed';
          // Use assignedFaction as primary source of truth (from API)
          if ((island as any).assignedFaction) {
            faction = (island as any).assignedFaction;
          } else if (island.ownerType === 'faction_regime') {
            faction = 'regime';
          } else if (island.factionId) {
            faction = island.factionId;
          } else if (island.ownerId?.startsWith('faction_')) {
            faction = island.ownerId.replace('faction_', '');
          } else if (island.ownerId) {
            faction = 'player';
          }
          stats[faction] = (stats[faction] || 0) + 1;
        });
        console.log('[FACTION MAP WRAPPER] Calculated faction stats:', stats, 'from', islands.length, 'islands');
        console.log('[FACTION MAP WRAPPER] Sample islands:', islands.slice(0, 3).map(i => ({ id: i.id, assignedFaction: (i as any).assignedFaction, factionId: i.factionId, ownerType: i.ownerType })));
        setFactionStats(stats);
      });
      
      scene.events.on('islandSelected', (island: IslandData) => {
        console.log('[FACTION MAP WRAPPER] Island selected event received:', island.id, island.permanentName || island.customName);
        setSelectedIsland(island);
      });
      
      scene.events.on('islandDeselected', () => {
        setSelectedIsland(null);
      });
    };

    const tryInit = (): void => {
      try {
        initScene();
      } catch (error) {
        console.error('[FACTION MAP] Failed to initialize scene:', error);
      }
    };

    // Try multiple initialization strategies (like WorldMap)
    const scene = gameRef.current.scene.getScene('FactionMapScene') as FactionMapScene | undefined;
    if (scene && scene.sys.isActive()) {
      tryInit();
    } else if (scene) {
      scene.events.once(Phaser.Scenes.Events.CREATE, tryInit);
      scene.events.once(Phaser.Scenes.Events.START, tryInit);
    }

    // Poll for scene initialization
    const initPoll = window.setInterval(() => {
      if (didInitSceneRef.current) {
        window.clearInterval(initPoll);
        return;
      }
      tryInit();
    }, 50);

    // Timeout fallback
    const initTimeout = window.setTimeout(() => {
      window.clearInterval(initPoll);
      if (!didInitSceneRef.current) {
        console.error('[FACTION MAP] Scene init timed out');
      }
    }, 3000);

    const handleResize = (): void => {
      if (gameRef.current) {
        gameRef.current.scale.resize(window.innerWidth, window.innerHeight - 100);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.clearInterval(initPoll);
      window.clearTimeout(initTimeout);
      window.removeEventListener('resize', handleResize);
      if (gameRef.current) gameRef.current.destroy(true);
      gameRef.current = null;
    };
  }, []);

  return (
    <div className="faction-warfare-container">
      <header className="faction-warfare-header">
        <div className="header-left">
          <h1 style={{ fontFamily: '"Sometype Mono", monospace', margin: 0, color: '#e2e8f0', fontSize: '24px', letterSpacing: '2px' }}>FACTION WARFARE</h1>
          <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '12px', fontFamily: '"Sometype Mono", monospace' }}>
            {territoryCount} TERRITORIES
          </span>
        </div>
        <div className="header-right">
          <button
            type="button"
            onClick={handleForceRefresh}
            disabled={isRefreshing}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: isRefreshing ? '#475569' : '#3b82f6',
              border: '1px solid ' + (isRefreshing ? '#64748b' : '#60a5fa'),
              color: '#fff',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              fontFamily: '"Sometype Mono", monospace',
              letterSpacing: '0.5px',
              transition: 'all 0.2s',
              opacity: isRefreshing ? 0.6 : 1,
            }}
            title="Force refresh map data"
          >
            {isRefreshing ? '⟳ REFRESHING...' : '⟳ REFRESH'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: '#dc2626',
              border: '1px solid #ef4444',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: '"Sometype Mono", monospace',
              marginLeft: '8px',
              letterSpacing: '0.5px',
            }}
          >
            ✕
          </button>
        </div>
      </header>

      <div className="faction-warfare-content">
        <div 
          id="faction-map-canvas" 
          style={{ 
            width: '100%', 
            height: '100%', 
            position: 'relative'
          }} 
        />
        
        {/* Instructions Panel */}
        {!selectedIsland && (
          <div style={{
            position: 'absolute',
            left: '20px',
            top: '20px',
            background: 'rgba(10, 14, 26, 0.95)',
            borderRadius: '0',
            padding: '16px',
            color: '#e2e8f0',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '11px',
            zIndex: 100,
            maxWidth: '280px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#fbbf24', marginBottom: '10px', letterSpacing: '0.5px', fontFamily: '"Sometype Mono", monospace' }}>MAP CONTROLS</div>
            <div style={{ marginBottom: '6px', color: '#94a3b8' }}>
              <span style={{ color: '#fbbf24' }}>🖱️ SCROLL</span> - ZOOM IN/OUT
            </div>
            <div style={{ marginBottom: '6px', color: '#94a3b8' }}>
              <span style={{ color: '#fbbf24' }}>RIGHT-CLICK + DRAG</span> - PAN MAP
            </div>
            <div style={{ marginBottom: '6px', color: '#94a3b8' }}>
              <span style={{ color: '#fbbf24' }}>HOVER ISLAND</span> - VIEW NAME
            </div>
            <div style={{ color: '#94a3b8' }}>
              <span style={{ color: '#fbbf24' }}>CLICK ISLAND</span> - VIEW DETAILS
            </div>
          </div>
        )}
        
        {/* Statistics Panel */}
        {!selectedIsland && Object.keys(factionStats).length > 0 && (
          <div style={{
            position: 'absolute',
            left: '20px',
            bottom: '20px',
            background: 'rgba(10, 14, 26, 0.95)',
            borderRadius: '0',
            padding: '16px',
            color: '#e2e8f0',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '11px',
            zIndex: 100,
            minWidth: '280px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#fbbf24', marginBottom: '10px', letterSpacing: '0.5px', fontFamily: '"Sometype Mono", monospace' }}>TERRITORY CONTROL</div>
            {Object.entries(factionStats)
              .sort((a, b) => b[1] - a[1])
              .map(([faction, count]) => (
                <div key={faction} style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#94a3b8', textTransform: 'uppercase' }}>
                    {faction === 'regime' ? '🏛️ REGIME' :
                     faction === 'navy' ? '⚓ NAVY' :
                     faction === 'pirate' ? '🏴‍☠️ PIRATES' :
                     faction === 'merchant' ? '💰 MERCHANTS' :
                     faction === 'smuggler' ? '🗡️ SMUGGLERS' :
                     faction === 'player' ? '👤 PLAYERS' :
                     '⚪ UNCLAIMED'}
                  </span>
                  <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>{count}</span>
                </div>
              ))}
          </div>
        )}
        
        {selectedIsland && (
          <div style={{
            position: 'absolute',
            right: '20px',
            top: '100px',
            width: '360px',
            background: 'rgba(10, 14, 26, 0.95)',
            border: '2px solid #fbbf24',
            borderRadius: '0',
            padding: '20px',
            color: '#e2e8f0',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '11px',
            zIndex: 100,
            maxHeight: '70vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
          }}>
            {/* Header */}
            <div style={{ marginBottom: '16px', borderBottom: '2px solid #475569', paddingBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fbbf24', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {selectedIsland.permanentName || selectedIsland.customName || 'UNNAMED ISLAND'}
                </div>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase' }}>
                  {selectedIsland.isPermanent && <span>📍 PERMANENT LOCATION</span>}
                  {!selectedIsland.isPermanent && <span>🏝️ PROCEDURAL ISLAND</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedIsland(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  marginLeft: '12px',
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Ownership Info */}
            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ color: '#64748b', marginBottom: '6px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>OWNER</div>
              <div style={{ 
                color: selectedIsland.ownerType === 'faction_regime' ? '#ef4444' : selectedIsland.ownerId ? '#06b6d4' : '#64748b', 
                fontWeight: 'bold',
                fontSize: '12px',
                textTransform: 'uppercase'
              }}>
                {selectedIsland.ownerType === 'faction_regime' 
                  ? '🏛️ THE REGIME' 
                  : selectedIsland.ownerName ? selectedIsland.ownerName.toUpperCase() : (selectedIsland.ownerId ? '👤 CLAIMED' : '⚪ UNCLAIMED')}
              </div>
            </div>
            
            {/* Faction Info */}
            {selectedIsland.ownerId && (
              <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
                <div style={{ color: '#64748b', marginBottom: '6px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>FACTION ALLEGIANCE</div>
                <div style={{ 
                  color: selectedIsland.factionId ? '#10b981' : '#94a3b8', 
                  fontWeight: 'bold',
                  fontSize: '12px',
                  textTransform: 'uppercase'
                }}>
                  {selectedIsland.factionId 
                    ? `⚔️ ${selectedIsland.factionId.toUpperCase()}`
                    : selectedIsland.ownerId?.startsWith('faction_') 
                      ? `⚔️ ${selectedIsland.ownerId.replace('faction_', '').toUpperCase()}`
                      : '👤 INDEPENDENT PLAYER'}
                </div>
              </div>
            )}
            
            {/* Island Stats */}
            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ color: '#64748b', marginBottom: '8px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>ISLAND PROPERTIES</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>TYPE</div>
                  <div style={{ color: '#e2e8f0', textTransform: 'uppercase' }}>{selectedIsland.type}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>BIOME</div>
                  <div style={{ color: '#e2e8f0', textTransform: 'uppercase' }}>{selectedIsland.biome}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>RADIUS</div>
                  <div style={{ color: '#e2e8f0' }}>{selectedIsland.radius.toFixed(0)}px</div>
                </div>
                {selectedIsland.claimRadius && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>CLAIM RADIUS</div>
                    <div style={{ color: '#e2e8f0' }}>{selectedIsland.claimRadius.toFixed(0)}px</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Coordinates */}
            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ color: '#64748b', marginBottom: '6px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px', fontFamily: '"Sometype Mono", monospace' }}>COORDINATES</div>
              <div style={{ color: '#e2e8f0', fontSize: '12px', fontFamily: '"Sometype Mono", monospace' }}>
                X: {selectedIsland.x.toFixed(0)} / Y: {selectedIsland.y.toFixed(0)}
              </div>
            </div>
            
            {/* Strategic Value */}
            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ color: '#64748b', marginBottom: '6px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px', fontFamily: '"Sometype Mono", monospace' }}>STRATEGIC VALUE</div>
              <div style={{ color: '#fbbf24', fontSize: '12px' }}>
                {selectedIsland.isPermanent ? '⭐⭐⭐ HIGH' : selectedIsland.claimRadius ? '⭐⭐ MEDIUM' : '⭐ LOW'}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '9px', marginTop: '4px' }}>
                {selectedIsland.isPermanent 
                  ? 'Permanent locations are key strategic points'
                  : selectedIsland.claimRadius 
                    ? 'Claimable territory with defensive value'
                    : 'Standard procedural island'}
              </div>
            </div>
            
            {/* Island ID */}
            <div>
              <div style={{ color: '#64748b', marginBottom: '4px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>ISLAND ID</div>
              <div style={{ fontSize: '9px', wordBreak: 'break-all', color: '#475569', fontFamily: 'Sometype Mono, monospace' }}>
                {selectedIsland.id}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="faction-warfare-legend" style={{ fontFamily: 'Sometype Mono, monospace' }}>
        <div className="legend-section">
          <span className="legend-title" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>FACTIONS</span>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#3b82f6' }} />
            <span style={{ textTransform: 'uppercase' }}>NAVY</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ef4444' }} />
            <span style={{ textTransform: 'uppercase' }}>PIRATES</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#10b981' }} />
            <span style={{ textTransform: 'uppercase' }}>MERCHANTS</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#8b5cf6' }} />
            <span style={{ textTransform: 'uppercase' }}>SMUGGLERS</span>
          </div>
        </div>
        <div className="legend-section">
          <span className="legend-title" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>CONTROL</span>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ef4444' }} />
            <span style={{ textTransform: 'uppercase' }}>REGIME</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#06b6d4' }} />
            <span style={{ textTransform: 'uppercase' }}>PLAYER CLAIMED</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#475569' }} />
            <span style={{ textTransform: 'uppercase' }}>UNCLAIMED</span>
          </div>
        </div>
      </div>
    </div>
  );
};
